import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { getPrintableGroups } from "../lib/api";
import { printPage } from "../lib/print";

// Full name as "Last, First" — first_name from the API is already the preferred name when set.
const rosterName = (s) => `${s.last_name || ""}, ${s.first_name || ""}`.replace(/^,\s*|,\s*$/g, "").trim();

const byName = (a, b) =>
  (a.last_name || "").toLowerCase().localeCompare((b.last_name || "").toLowerCase()) ||
  (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());

const PrinterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
    <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect width="12" height="8" x="6" y="14" />
  </svg>
);

// Printable roster of current students grouped by grade level.
export default function PrintableGradeRoster() {
  const [grades, setGrades] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPrintableGroups("grade")
      .then((r) => { if (active) setGrades(r.data.grade || {}); })
      .catch(() => { if (active) toast.error("Failed to load grade level list"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const entries = grades ? Object.entries(grades).filter(([, g]) => g.students.length > 0) : [];

  const handlePrint = () => {
    if (!entries.length) return;
    let content = "";
    entries.forEach(([, g]) => {
      const rows = [...g.students].sort(byName).map((s, i) =>
        `<tr><td style="width:36px;text-align:center;color:#666;">${i + 1}</td><td>${rosterName(s)}</td><td style="width:110px;">${s.student_number || ""}</td></tr>`
      ).join("");
      content += `<div class="grade-block"><h2>${g.name} <span style="font-weight:normal;color:#666;font-size:12px;">(${g.students.length})</span></h2>`
        + `<table><thead><tr><th style="width:36px;">#</th><th>Student Name</th><th style="width:110px;">Student #</th></tr></thead><tbody>${rows}</tbody></table></div>`;
    });
    printPage({
      title: "Student List by Grade Level",
      content,
      extraStyles: `.grade-block { page-break-inside: avoid; margin-bottom: 20px; }`,
    });
  };

  return (
    <Card data-testid="printable-grade-roster">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Printable List by Grade Level</CardTitle>
            <CardDescription>Each grade with a full-name roster (Last, First), alphabetized</CardDescription>
          </div>
          <Button onClick={handlePrint} disabled={loading || entries.length === 0} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="print-grade-roster-btn">
            <PrinterIcon />Print
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]"></div></div>
        ) : entries.length === 0 ? (
          <p className="text-center text-muted-foreground py-8" data-testid="grade-roster-empty">No current students to list by grade.</p>
        ) : (
          <div className="space-y-6">
            {entries.map(([key, g]) => (
              <div key={key} data-testid={`grade-block-${key}`}>
                <h3 className="text-base font-semibold text-[#00305E] mb-2">
                  {g.name} <span className="text-sm font-normal text-muted-foreground">({g.students.length})</span>
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 w-12 font-medium">#</th>
                        <th className="text-left py-2 px-3 font-medium">Student Name</th>
                        <th className="text-left py-2 px-3 w-32 font-medium">Student #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...g.students].sort(byName).map((s, i) => (
                        <tr key={s.id} className={i % 2 ? "bg-gray-50" : "bg-white"} data-testid={`grade-student-${s.id}`}>
                          <td className="py-1.5 px-3 text-muted-foreground">{i + 1}</td>
                          <td className="py-1.5 px-3 font-medium">{rosterName(s)}</td>
                          <td className="py-1.5 px-3 text-muted-foreground">{s.student_number || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
